// One way to call the API from a screen: the caller's Firebase ID token as a
// Bearer credential, and the selected workspace as X-Tenant-Id. The server
// (apps/api/src/server.js) treats X-Tenant-Id as a request to act in that
// workspace and refuses it with 403 unless the caller is a member, so the
// header can only ever narrow access, never widen it. With no workspace
// selected the header is omitted and the server uses the caller's first
// membership, exactly as /api/me reports it.

import { isOnline, OFFLINE_WRITE } from './connection'
import { actingFor } from './actAs'
import { currentRelease } from './release'

// Which app and release this is, for Personnel's badges (166; services/client-apps.js).
const CLIENT = `control/web/${currentRelease().commit.slice(0, 7) || 'dev'}`

export interface TokenSource {
  getIdToken: () => Promise<string>
}

let activeTenantId: string | null = null
const tenantChanges = new Set<() => void>()

// Set by WorkspaceContext when the viewer picks a workspace.
export function setActiveTenantId(id: string | null) {
  if (id === activeTenantId) return
  activeTenantId = id
  tenantChanges.forEach((fn) => fn())
}

// For what outlives a request (the live stream): told when the workspace
// changes, so it can reopen for the new one.
export function onActiveTenantChange(fn: () => void) {
  tenantChanges.add(fn)
  return () => {
    tenantChanges.delete(fn)
  }
}

export function getActiveTenantId() {
  return activeTenantId
}

export class ApiError extends Error {
  status: number
  body: any
  constructor(status: number, body: any) {
    super((body && (body.error || body.message)) || `Request failed (${status})`)
    this.status = status
    this.body = body
  }
}

// A flow of several requests that goes on after its first one (a recalculation that drains and then polls,
// a draft batch followed by its generate) belongs to the workspace it began in. Every request names the
// workspace current when it is made, so a later step of that flow, made after the viewer switched, would
// be addressed to the new one. Binding the sign-in source at the start of the flow pins every step to the
// workspace the person was in when they asked; the server still refuses it unless they are a member.
export interface BoundSource extends TokenSource {
  uid?: string
  boundTenantId: string | null
}

export function inCurrentWorkspace(user: TokenSource & { uid?: string }): BoundSource
export function inCurrentWorkspace(user: null | undefined): null | undefined
export function inCurrentWorkspace(user: (TokenSource & { uid?: string }) | null | undefined): BoundSource | null | undefined
export function inCurrentWorkspace(user: (TokenSource & { uid?: string }) | null | undefined) {
  if (!user) return user
  if ('boundTenantId' in user) return user as BoundSource
  return { getIdToken: () => user.getIdToken(), uid: user.uid, boundTenantId: activeTenantId }
}

// A sign-in Firebase will no longer renew: the account was deleted or disabled, its sessions were revoked,
// or (locally) the Auth emulator restarted and forgot it. Retrying cannot help, so rather than every screen
// showing Firebase's own words, the request fails with SESSION_EXPIRED and AuthContext signs the person out
// and shows the sign-in screen with that sentence.
const DEAD_SESSION_CODES = new Set([
  'auth/invalid-refresh-token',
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/user-disabled',
  'auth/user-not-found',
])

export const SESSION_EXPIRED = 'Your sign-in has expired. Sign in again to carry on.'

export class SessionExpiredError extends Error {
  constructor() {
    super(SESSION_EXPIRED)
    this.name = 'SessionExpiredError'
  }
}

export function isDeadSession(err: unknown) {
  return DEAD_SESSION_CODES.has(String((err as { code?: unknown } | null)?.code ?? ''))
}

const sessionExpiries = new Set<() => void>()

// For AuthContext: told when a request finds the sign-in can no longer be renewed.
export function onSessionExpired(fn: () => void) {
  sessionExpiries.add(fn)
  return () => {
    sessionExpiries.delete(fn)
  }
}

async function bearer(user: TokenSource) {
  try {
    return await user.getIdToken()
  } catch (err) {
    if (!isDeadSession(err)) throw err
    sessionExpiries.forEach((fn) => fn())
    throw new SessionExpiredError()
  }
}

export async function apiFetch(user: TokenSource | null | undefined, path: string, init: RequestInit = {}): Promise<Response> {
  const headers = new Headers(init.headers || {})
  // The workspace is read now, before the token is awaited: a write made in one workspace must not be
  // re-addressed to another the viewer picked while the token was being fetched. A source bound to a
  // workspace (inCurrentWorkspace) keeps naming that one for as long as the flow runs.
  const tenantId = user && 'boundTenantId' in user ? (user as BoundSource).boundTenantId : activeTenantId
  if (user) headers.set('Authorization', `Bearer ${await bearer(user)}`)
  if (tenantId) headers.set('X-Tenant-Id', tenantId)
  headers.set('X-Dataphyll-Client', CLIENT)
  // A SYSADMIN acting as someone in this workspace (lib/actAs.ts): the server checks it again on every request.
  const acting = user ? actingFor((user as { uid?: string }).uid, tenantId) : null
  if (acting && !headers.has('X-Act-As')) headers.set('X-Act-As', acting.membershipId)
  if (init.body !== undefined && !headers.has('Content-Type')) headers.set('Content-Type', 'application/json')
  return fetch(path, { ...init, headers })
}

export const UNREACHABLE_WRITE = 'The server could not be reached, so nothing was saved. Nothing you have entered is lost: check the connection and try again.'
export const UNREACHABLE_READ = 'The server could not be reached. Check the connection and try again.'
export const SERVER_FAULT_WRITE = 'The server hit a problem, so nothing was saved. Nothing you have entered is lost: try again, and tell Dataphyll support if it happens again.'
export const SERVER_FAULT_READ = 'The server hit a problem loading this. Try again, and tell Dataphyll support if it happens again.'

// JSON in, JSON out; a non-2xx response throws ApiError carrying the server's
// body, so a screen can act on its `code` (SHIFT_EXISTS, DAY_LOCKED, …).
//
// With no connection a write is refused here, before it is sent (h 4071-4073):
// it throws ApiError with status 0 and code OFFLINE, which the screen shows
// where the action was, keeping what was typed. A read is still attempted.
export async function apiJson<T = any>(user: TokenSource | null | undefined, path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const method = init.method || (init.body !== undefined ? 'POST' : 'GET')
  const write = method !== 'GET'
  if (write && !isOnline()) throw new ApiError(0, { code: 'OFFLINE', error: OFFLINE_WRITE })
  let res: Response
  try {
    res = await apiFetch(user, path, {
      method,
      body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
    })
  } catch (e) {
    // fetch rejects with a TypeError when the request never got an answer:
    // say so in words, not "Failed to fetch". Anything else (the sign-in
    // token) is not a network fault and goes up as it is.
    if (!(e instanceof TypeError)) throw e
    throw new ApiError(0, { code: 'UNREACHABLE', error: write ? UNREACHABLE_WRITE : UNREACHABLE_READ })
  }
  let body: any = null
  try {
    body = await res.json()
  } catch {
    body = null
  }
  if (!res.ok) {
    // A server fault with nothing to say for itself still says what did not
    // happen and what to do (h 4067-4070).
    if (res.status >= 500 && !(body && (body.error || body.message))) {
      body = { ...(body || {}), error: write ? SERVER_FAULT_WRITE : SERVER_FAULT_READ }
    }
    throw new ApiError(res.status, body)
  }
  return body as T
}
